import React, { useState, useEffect } from "react"
import { Modal, Button, Space, message, Typography, Tooltip } from "antd"
import { ArrowUp, ArrowDown, RotateCcw, Check, Menu, Eye, EyeOff } from "react-feather"
import { useTranslation } from "react-i18next"
import menuItems, { MenuItem } from "../../data/menuItems"

export const MENU_ORDER_STORAGE_KEY = "crm_custom_menu_order"
export const MENU_HIDDEN_STORAGE_KEY = "crm_custom_menu_hidden"

export function getCustomMenuItems(): MenuItem[] {
  try {
    const savedOrder = localStorage.getItem(MENU_ORDER_STORAGE_KEY)
    const savedHidden = localStorage.getItem(MENU_HIDDEN_STORAGE_KEY)
    const hiddenRoutes: string[] = savedHidden ? JSON.parse(savedHidden) : []

    if (!savedOrder) {
      return menuItems.filter((item) => !hiddenRoutes.includes(item.route))
    }

    const orderRoutes: string[] = JSON.parse(savedOrder)
    const orderedItems: MenuItem[] = []

    orderRoutes.forEach((route) => {
      const item = menuItems.find((m) => m.route === route)
      if (item && !hiddenRoutes.includes(item.route)) {
        orderedItems.push(item)
      }
    })

    // Add any new menu items that weren't in saved order
    menuItems.forEach((item) => {
      if (
        !orderRoutes.includes(item.route) &&
        !hiddenRoutes.includes(item.route) &&
        !orderedItems.some((m) => m.route === item.route)
      ) {
        orderedItems.push(item)
      }
    })

    return orderedItems
  } catch {
    return menuItems
  }
}

export default function MenuReorderModal({
  visible,
  onClose,
  onOrderChanged,
}: {
  visible: boolean
  onClose: () => void
  onOrderChanged?: () => void
}) {
  const { t } = useTranslation()
  const [items, setItems] = useState<MenuItem[]>([])
  const [hiddenRoutes, setHiddenRoutes] = useState<string[]>([])
  const [dragIndex, setDragIndex] = useState<number | null>(null)

  useEffect(() => {
    if (visible) {
      try {
        const savedOrder = localStorage.getItem(MENU_ORDER_STORAGE_KEY)
        const savedHidden = localStorage.getItem(MENU_HIDDEN_STORAGE_KEY)
        const hidden: string[] = savedHidden ? JSON.parse(savedHidden) : []
        setHiddenRoutes(hidden)

        if (savedOrder) {
          const orderRoutes: string[] = JSON.parse(savedOrder)
          const list: MenuItem[] = []
          orderRoutes.forEach((route) => {
            const it = menuItems.find((m) => m.route === route)
            if (it) list.push(it)
          })
          menuItems.forEach((it) => {
            if (!orderRoutes.includes(it.route)) list.push(it)
          })
          setItems(list)
        } else {
          setItems([...menuItems])
        }
      } catch {
        setItems([...menuItems])
      }
    }
  }, [visible])

  const moveItem = (index: number, direction: "up" | "down") => {
    const newItems = [...items]
    const targetIndex = direction === "up" ? index - 1 : index + 1
    if (targetIndex < 0 || targetIndex >= newItems.length) return

    const temp = newItems[index]
    newItems[index] = newItems[targetIndex]
    newItems[targetIndex] = temp
    setItems(newItems)
  }

  const toggleHidden = (route: string) => {
    if (hiddenRoutes.includes(route)) {
      setHiddenRoutes(hiddenRoutes.filter((r) => r !== route))
    } else {
      setHiddenRoutes([...hiddenRoutes, route])
    }
  }

  const handleDragStart = (idx: number) => {
    setDragIndex(idx)
  }

  const handleDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault()
    if (dragIndex === null || dragIndex === idx) return
    const newItems = [...items]
    const draggedItem = newItems[dragIndex]
    newItems.splice(dragIndex, 1)
    newItems.splice(idx, 0, draggedItem)
    setDragIndex(idx)
    setItems(newItems)
  }

  const handleSave = () => {
    try {
      const orderRoutes = items.map((i) => i.route)
      localStorage.setItem(MENU_ORDER_STORAGE_KEY, JSON.stringify(orderRoutes))
      localStorage.setItem(MENU_HIDDEN_STORAGE_KEY, JSON.stringify(hiddenRoutes))
      message.success(t("msgMenuOrderSaved"))
      if (onOrderChanged) onOrderChanged()
      onClose()
    } catch {
      message.error(t("msgSaveFailed"))
    }
  }

  const handleReset = () => {
    localStorage.removeItem(MENU_ORDER_STORAGE_KEY)
    localStorage.removeItem(MENU_HIDDEN_STORAGE_KEY)
    setItems([...menuItems])
    setHiddenRoutes([])
    message.info("Menyu tartibi standart holatga qaytarildi")
    if (onOrderChanged) onOrderChanged()
    onClose()
  }

  return (
    <Modal
      title={
        <Space align="center">
          <Menu size={20} color="#2563EB" />
          <Typography.Title level={5} style={{ margin: 0 }}>
            Menyular Tartibini Sozlash (Drag & Drop)
          </Typography.Title>
        </Space>
      }
      visible={visible}
      onCancel={onClose}
      width={560}
      footer={
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Button icon={<RotateCcw size={14} style={{ marginRight: 4 }} />} onClick={handleReset}>
            Standartga qaytarish
          </Button>
          <Space>
            <Button onClick={onClose}>{t("cancel")}</Button>
            <Button
              type="primary"
              icon={<Check size={14} style={{ marginRight: 4 }} />}
              onClick={handleSave}
              style={{ background: "#2563EB", borderColor: "#2563EB" }}
            >
              Saqlash
            </Button>
          </Space>
        </div>
      }
    >
      <Typography.Paragraph type="secondary" style={{ fontSize: "13px", marginBottom: 12 }}>
        Bo'limlarni surib o'zgartirish uchun qatorni sudrab olib boring (Drag & Drop) yoki o'ng tomondagi tugmalardan foydalaning:
      </Typography.Paragraph>

      <div style={{ maxHeight: "420px", overflowY: "auto", paddingRight: 4 }}>
        {items.map((item, index) => {
          const isHidden = hiddenRoutes.includes(item.route)
          const IconComp = item.icon

          return (
            <div
              key={item.route}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={() => setDragIndex(null)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 12px",
                marginBottom: 6,
                borderRadius: 8,
                background: isHidden
                  ? "rgba(100, 116, 139, 0.08)"
                  : dragIndex === index
                  ? "#EFF6FF"
                  : "#F8FAFC",
                border:
                  dragIndex === index
                    ? "2px dashed #2563EB"
                    : isHidden
                    ? "1px dashed #CBD5E1"
                    : "1px solid #E2E8F0",
                cursor: "grab",
                transition: "all 0.2s ease",
                opacity: isHidden ? 0.6 : 1,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ color: "#94A3B8", fontSize: "12px", width: 20 }}>
                  {index + 1}.
                </span>
                <span
                  style={{
                    display: "inline-flex",
                    padding: 6,
                    borderRadius: 6,
                    background: isHidden ? "#E2E8F0" : "#DBEAFE",
                    color: isHidden ? "#64748B" : "#2563EB",
                  }}
                >
                  <IconComp size={16} />
                </span>
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: "14px",
                    color: isHidden ? "#64748B" : "#1E293B",
                    textDecoration: isHidden ? "line-through" : "none",
                  }}
                >
                  {t(item.title)}
                </span>
              </div>

              <Space size={4}>
                <Tooltip title={isHidden ? "Ko'rsatish" : "Yashirish"}>
                  <Button
                    size="small"
                    type="text"
                    icon={
                      isHidden ? (
                        <EyeOff size={14} color="#EF4444" />
                      ) : (
                        <Eye size={14} color="#10B981" />
                      )
                    }
                    onClick={() => toggleHidden(item.route)}
                  />
                </Tooltip>
                <Tooltip title={t("tipMoveUp")}>
                  <Button
                    size="small"
                    type="text"
                    disabled={index === 0}
                    icon={<ArrowUp size={14} />}
                    onClick={() => moveItem(index, "up")}
                  />
                </Tooltip>
                <Tooltip title={t("tipMoveDown")}>
                  <Button
                    size="small"
                    type="text"
                    disabled={index === items.length - 1}
                    icon={<ArrowDown size={14} />}
                    onClick={() => moveItem(index, "down")}
                  />
                </Tooltip>
              </Space>
            </div>
          )
        })}
      </div>
    </Modal>
  )
}
